import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/** Key used when Entra ID is not configured (local, unauthenticated mode). */
const ANONYMOUS = '__anonymous__'
const SIX_MONTHS_IN_MS = 180 * 24 * 60 * 60 * 1000

interface OnboardingState {
  /** Entra object ID -> Expiration timestamp in milliseconds */
  seenBy: Record<string, number>
  hasSeen: (userId: string | null) => boolean
  markSeen: (userId: string | null) => void
  reset: () => void
}

/**
 * Tracks whether the one-time welcome screen was already shown. Kept per user
 * so a shared machine does not hide it from the next person signing in.
 *
 * This lives in localStorage; once the backend exposes a user-preferences
 * endpoint, move the flag there and keep this store as the cache.
 */
export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      seenBy: {},
      hasSeen: (userId) => {
        const key = userId ?? ANONYMOUS
        const expiryTimestamp = get().seenBy[key]

        if (!expiryTimestamp || Date.now() > expiryTimestamp) {
          return false
        }

        return true
      },
      markSeen: (userId) => {
        const key = userId ?? ANONYMOUS
        const newExpiry = Date.now() + SIX_MONTHS_IN_MS

        set((state) => ({
          seenBy: { ...state.seenBy, [key]: newExpiry },
        }))
      },
      reset: () => {
        set({ seenBy: {} })
      },
    }),
    { name: 'corvinus-onboarding', partialize: (state) => ({ seenBy: state.seenBy }) },
  ),
)
